interface ProgressAvatarProps {
	size?: number;
	progress?: number;
	level?: number;
	imageUrl?: string;
	gradientId?: string;
	levelPosition?: 'top' | 'bottom' | 'left' | 'right' | 'bottom-right';
}

export default function ProgressAvatar({
	size = 150,
	progress = 75,
	level = 1,
	imageUrl = require('#GameFrontend/authorized/assets/ui/example_avatar.png'),
	gradientId = 'progressGradient',
	levelPosition = 'top',
}: ProgressAvatarProps) {
	// Calculate dimensions based on size
	const radius = size / 2;
	const borderWidth = size * 0.1; // 10% of size for thicker border
	const innerPadding = size * 0.1; // 10% of size
	const levelBadgeSize = size * 0.25 < 25 ? 25 : size * 0.25; // 25% of size or min 25px
	const levelBadgeBorder = size * 0.015; // 1% of size

	// Calculate the actual radius for the progress circle
	const progressRadius = radius < 70 ? radius - 4 : radius - 8 - borderWidth;
	// Calculate circumference based on the progress radius
	const circumference = 2 * Math.PI * progressRadius;

	// Check if this is the Layout ProgressAvatar
	const isLayoutAvatar = gradientId === 'layoutProgressGradient';

	// Calculate offset based on progress percentage (0-100)
	// For Layout: 100% should show 63% of the circle due to being cut off
	// For BottomBar: 100% should show full circle
	const adjustedProgress = isLayoutAvatar ? progress * 0.63 : progress;
	const offset = ((100 - adjustedProgress) / 100) * circumference;

	// Calculate level badge position based on levelPosition prop
	const getLevelBadgePosition = () => {
		switch (levelPosition) {
			case 'top':
				return {
					top: '-14px',
					left: '50%',
					transform: 'translateX(-50%)',
				};
			case 'bottom':
				return {
					bottom: `-${levelBadgeSize / 2}px`,
					left: '50%',
					transform: 'translateX(-50%)',
				};
			case 'left':
				return {
					left: `-${levelBadgeSize / 2}px`,
					top: '50%',
					transform: 'translateY(-50%)',
				};
			case 'right':
				return {
					right: `-${levelBadgeSize / 2}px`,
					top: '50%',
					transform: 'translateY(-50%)',
				};
			case 'bottom-right':
				return {
					bottom: '2px',
					right: '2px',
					transform: 'none',
				};
			default:
				return {
					top: `-${levelBadgeSize / 2}px`,
					left: '50%',
					transform: 'translateX(-50%)',
				};
		}
	};

	return (
		<div
			className="relative lg:absolute lg:bottom-5 lg:left-1/2 lg:-translate-x-1/2 lg:z-30"
			style={{ width: size, height: size }}
		>
			<div className="relative w-full h-full">
				{/* Progress ring container */}
				<div className="absolute inset-0 rounded-full">
					{/* Base ring with 3D effect */}
					<div className="absolute inset-0 rounded-full bg-gradient-to-b from-amber-900/90 to-amber-950/90 shadow-md"></div>

					{/* Outer glow effect */}
					<div className="absolute inset-0 rounded-full bg-gradient-to-r from-amber-400 to-amber-600 opacity-50 blur-sm"></div>

					{/* SVG Progress Circle */}
					<svg className="absolute inset-0 w-full h-full -rotate-90 overflow-visible">
						{/* Background circle */}
						<circle
							cx={radius}
							cy={radius}
							r={radius - 10}
							stroke="rgba(146, 64, 14, 0.3)"
							strokeWidth={borderWidth}
							fill="none"
							className="drop-shadow-lg"
						/>
						{/* Progress circle */}
						<circle
							cx={radius}
							cy={radius}
							r={radius < 70 ? radius - 4 : radius - 8}
							stroke={`url(#${gradientId})`}
							strokeWidth={borderWidth}
							strokeLinecap="round"
							fill="none"
							strokeDasharray={circumference}
							strokeDashoffset={offset}
							className="transition-all duration-1000 ease-out filter drop-shadow-[0_0_8px_rgba(255,255,255,0.1)]"
						/>
						{/* Gradient definition */}
						<defs>
							<linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
								<stop offset="0%" stopColor="#0a7aa4" />
								<stop offset="33%" stopColor="#e617ff" />
								<stop offset="66%" stopColor="#5a2bf2" />
								<stop offset="100%" stopColor="#9517ff" />
							</linearGradient>
						</defs>
					</svg>

					{/* Circular shadow effect */}
					<div className="absolute inset-0">
						<div className="absolute inset-0 rounded-full bg-gradient-to-b from-black/30 via-transparent to-black/40"></div>
						<div className="absolute inset-0 rounded-full bg-gradient-to-t from-white/10 to-transparent"></div>
					</div>
				</div>

				{/* Inner container with border effect */}
				<div
					className="absolute bg-gradient-to-b from-white to-gray-100 rounded-full overflow-hidden shadow-inner"
					style={{
						inset: innerPadding,
					}}
				>
					{/* Image container */}
					<div
						className="absolute rounded-full overflow-hidden"
						style={{
							inset: innerPadding - 5,
							transform: 'scale(1.1)',
						}}
					>
						<img src={imageUrl} alt="Avatar" className="w-full h-full object-cover" />
					</div>
				</div>

				{/* Level indicator */}
				<div
					className="absolute bg-[#fb923c] rounded-full shadow-lg flex items-center justify-center border border-white"
					style={{
						...getLevelBadgePosition(),
						width: levelBadgeSize,
						height: levelBadgeSize,
						borderWidth: levelBadgeBorder,
					}}
				>
					<span className="text-white font-bold drop-shadow-md" style={{ fontSize: levelBadgeSize * 0.4 }}>
						{level}
					</span>
				</div>
			</div>
		</div>
	);
}
